import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { ActivityTemplateService } from '../activity-template/activity-template.service';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { SourceTypesLookup } from '@go-modules/interfaces/source-type-lookup';
import { TitleDescription } from '@go-modules/interfaces/title-description';
import { NgxSourceMediaPanelComponent } from '@go-modules/components/sidepanels/activity-editor/source-media-panel/source-media-panel.component';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { ActivitySourceMediaModel } from 'go-modules/models/activity/activity-source-media';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { LibraryDialogComponent } from '@go-modules/components/dialogs/library-dialog/library-dialog.component';
import { ActivityTemplateModel } from 'go-modules/models/activity-template/activity-template.schema';

@Component({
	selector: 'source-media-setting',
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCheckboxModule,
		MatSelectModule,
		NgxSourceMediaPanelComponent
	],
	templateUrl: './source-media-setting.component.html',
	styleUrls: ['./source-media-setting.component.scss'],
	providers: [ActivityTemplateService],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SourceMediaSettingComponent implements OnInit {
	@Input() public activity: ActivityModel;
	@Input() public selectedTemplate: ActivityTemplateModel;
	@Input() public sourceMediaControl: FormControl;
	@Input() public hasSelfSelectSourceMediaControl: FormControl;
	@Input() public sourceTypeControl: FormControl<string | null>;

	public sourceTypesLookup: SourceTypesLookup;
	public sourceTypes: TitleDescription[];

	constructor (
		@Inject(mediaToken) private MediaModel,
		private cdr: ChangeDetectorRef,
		private ngxGoModalService: GoModalService
	) {
		this.sourceTypesLookup = {
			INSTRUCTOR_SELECTED: {
				title: 'activity-editor-new_source-media-instructor-selected-title',
				description: 'activity-editor-new_source-media-instructor-selected-description'
			},
			PRESENTER_SELECTED: {
				title: 'activity-editor-new_source-media-presenter-selected-title',
				description: 'activity-editor-new_source-media-presenter-selected-description'
			}
		};

		this.sourceTypes = [
			this.sourceTypesLookup.INSTRUCTOR_SELECTED,
			this.sourceTypesLookup.PRESENTER_SELECTED
		];
	}

	public ngOnInit () {
		const sourceType = this.hasSelfSelectSourceMediaControl.value
			? this.sourceTypesLookup.PRESENTER_SELECTED.title
			: (
				this.sourceMediaControl.value
					? this.sourceTypesLookup.INSTRUCTOR_SELECTED.title
					: null
			);

		this.sourceTypeControl.setValue(sourceType);
	}

	public shouldShowSourceTypeDropdown (): boolean {
		return !!this.selectedTemplate?.isActive?.('has_response_media');
	}

	public shouldShowSourceMediaButton (): boolean {
		if (!this.selectedTemplate) {
			return false;
		}

		return !this.selectedTemplate.isActive?.('has_response_media') &&
			!this.sourceMediaControl.value;
	}

	public onSourceTypeChange (change: MatSelectChange): void {
		this.sourceTypeControl.setValue(change.value);


		if (this.sourceTypeControl.value === this.sourceTypesLookup.INSTRUCTOR_SELECTED.title) {
			this.hasSelfSelectSourceMediaControl.setValue(false);

			this.openSourceMediaModal();
		}

		if (this.sourceTypeControl.value === this.sourceTypesLookup.PRESENTER_SELECTED.title) {
			this.sourceMediaControl.setValue(null);
			this.hasSelfSelectSourceMediaControl.setValue(true);
		}
	}

	public handleOnRemoveSourceMedia () {
		this.sourceMediaControl.setValue(null);
		this.openSourceMediaModal();
	}

	public openSourceMediaModal () {
		this.ngxGoModalService.open(LibraryDialogComponent, false, {
			data: {
				filterType: [CONTENT_TYPES.MEDIA, CONTENT_TYPES.DOCUMENTS],
				mode: MODES.SELECT,
				previewable: true,
				collectionManagement: true
			}
		}).afterClosed().subscribe({
			next: (res: any) => {
				if (res) {
					const media = this.MediaModel.model(res);
					const sourceMedia = ActivitySourceMediaModel.create({ media, media_id: media.media_id });

					this.updateSourceMedia(sourceMedia);

					this.sourceTypeControl.markAsDirty();
				} else {
					this.removeSourceMedia();
				}
			},
			error: () => {
				this.removeSourceMedia();
			}
		});
	}

	public handleMediaSync (event) {
		this.updateSourceMedia(event);
	}

	public getSelectedSourceType (): TitleDescription | undefined {
		const selectedKey = this.sourceTypeControl.value;
		if (!selectedKey) return undefined;
		return this.sourceTypes.find((s) => s.title === selectedKey);
	}

	private removeSourceMedia (): void {
		this.sourceMediaControl.setValue(null);
		this.sourceTypeControl.setValue(null);
	}

	private updateSourceMedia (sourceMedia): void {
		this.sourceMediaControl.setValue(sourceMedia);
		this.cdr.markForCheck();
	}
}
